import LegalLayout from '../components/LegalLayout';

const Article = ({ num, title, children }: { num: string; title: string; children: React.ReactNode }) => (
  <article className="mb-10">
    <h2 className="text-lg font-bold text-[#C9A227] mb-4 flex items-center gap-3">
      <span className="text-xs font-mono text-white/20 w-8">ART.</span>
      <span>{num} — {title}</span>
    </h2>
    <div className="pl-11 space-y-3 text-white/70 text-sm leading-relaxed">
      {children}
    </div>
  </article>
);

interface CookieRowProps {
  name: string;
  category: string;
  purpose: string;
  duration: string;
  legalBasis: string;
}

const CookieRow = ({ name, category, purpose, duration, legalBasis }: CookieRowProps) => (
  <tr className="border-t border-white/[0.05]">
    <td className="py-3 pr-4 font-mono text-xs text-[#C9A227]">{name}</td>
    <td className="py-3 pr-4 text-xs text-white/60">{category}</td>
    <td className="py-3 pr-4 text-xs text-white/60">{purpose}</td>
    <td className="py-3 pr-4 text-xs text-white/60">{duration}</td>
    <td className="py-3 text-xs text-white/60">{legalBasis}</td>
  </tr>
);

export default function CookiesPage() {
  return (
    <LegalLayout
      title="Politique de gestion des cookies"
      subtitle="Conforme au référentiel CNIL du 1er octobre 2020 — lereseausportif.com"
    >
      <div className="bg-[#0D1228] border border-white/[0.07] rounded-lg p-6 mb-10 text-xs text-white/40 flex items-start gap-3">
        <i className="ri-cookie-line text-[#C9A227] text-base mt-0.5 shrink-0"></i>
        <p>
          La présente politique explique ce que sont les cookies, lesquels sont utilisés sur le site <strong className="text-white/60">lereseausportif.com</strong> (ci-après « le Site »), leurs finalités, leur durée de conservation et la manière dont vous pouvez les paramétrer.
          Elle a été élaborée conformément au référentiel publié par la <strong className="text-white/60">Commission Nationale de l&apos;Informatique et des Libertés (CNIL)</strong> le 1er octobre 2020.
        </p>
      </div>

      <Article num="1" title="Qu'est-ce qu'un cookie ?">
        <p>
          Un cookie (ou traceur) est un petit fichier texte déposé sur le terminal de l&apos;utilisateur (ordinateur, tablette, smartphone) lors de sa navigation sur un site internet. Il est lu par le serveur du site lors des visites ultérieures.
        </p>
        <p>Les cookies permettent notamment de :</p>
        <ul className="list-disc list-inside space-y-1 text-white/60 pl-2">
          <li>Reconnaître un utilisateur lors de sa connexion et maintenir sa session active</li>
          <li>Mémoriser ses préférences et paramètres</li>
          <li>Mesurer l&apos;audience du site et analyser les comportements de navigation</li>
          <li>Assurer la sécurité de l&apos;authentification</li>
        </ul>
        <p>
          Les cookies ne contiennent pas d&apos;informations personnelles permettant d&apos;identifier l&apos;utilisateur en dehors de la plateforme et ne peuvent pas accéder aux données stockées sur votre terminal.
        </p>
      </Article>

      <Article num="2" title="Cookies utilisés sur lereseausportif.com">
        <p className="font-semibold text-white/80 mb-3">2.1 — Cookies strictement nécessaires (essentiels)</p>
        <p>
          Ces cookies sont indispensables au fonctionnement du site. Ils ne nécessitent pas votre consentement conformément au référentiel CNIL (article 82 de la loi Informatique et Libertés).
        </p>
        <div className="overflow-x-auto mt-3">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="text-left">
                <th className="pb-3 pr-4 text-xs text-white/30 uppercase tracking-wide">Nom</th>
                <th className="pb-3 pr-4 text-xs text-white/30 uppercase tracking-wide">Catégorie</th>
                <th className="pb-3 pr-4 text-xs text-white/30 uppercase tracking-wide">Finalité</th>
                <th className="pb-3 pr-4 text-xs text-white/30 uppercase tracking-wide">Durée</th>
                <th className="pb-3 text-xs text-white/30 uppercase tracking-wide">Base légale</th>
              </tr>
            </thead>
            <tbody>
              <CookieRow
                name="sb-auth-token"
                category="Essentiel"
                purpose="Maintien de la session d&apos;authentification utilisateur"
                duration="Session / 7 jours"
                legalBasis="Intérêt légitime / Exécution du contrat"
              />
              <CookieRow
                name="sb-refresh-token"
                category="Essentiel"
                purpose="Renouvellement automatique du jeton d&apos;accès"
                duration="30 jours"
                legalBasis="Intérêt légitime / Exécution du contrat"
              />
              <CookieRow
                name="app-preferences"
                category="Essentiel"
                purpose="Mémorisation des préférences d&apos;interface (thème, langue)"
                duration="12 mois"
                legalBasis="Intérêt légitime"
              />
              <CookieRow
                name="csrf-token"
                category="Sécurité"
                purpose="Protection contre les attaques CSRF"
                duration="Session"
                legalBasis="Intérêt légitime (sécurité)"
              />
            </tbody>
          </table>
        </div>

        <p className="font-semibold text-white/80 mt-6 mb-3">2.2 — Cookies de mesure d&apos;audience (analytics)</p>
        <p>
          Ces cookies permettent de mesurer la fréquentation du site et d&apos;analyser les comportements de navigation dans le but d&apos;améliorer les services proposés. Ils <strong className="text-white/70">requièrent votre consentement</strong> préalable.
        </p>
        <div className="bg-[#080C1E] rounded-lg border border-white/[0.05] p-4 mt-3">
          <p className="text-white/40 text-xs">
            <strong className="text-white/60">Outil analytics utilisé :</strong> [À préciser — ex. : Google Analytics 4, Matomo, Plausible…]<br />
            Le tableau des cookies analytics sera mis à jour dès la configuration de l&apos;outil retenu.<br />
            À ce jour, aucun cookie de mesure d&apos;audience n&apos;est déposé sans votre consentement.
          </p>
        </div>

        <p className="font-semibold text-white/80 mt-6 mb-3">2.3 — Cookies de réseaux sociaux et de partage</p>
        <p>
          Le site lereseausportif.com peut intégrer des boutons ou modules de partage vers des réseaux sociaux tiers (LinkedIn, Instagram…). Ces modules sont susceptibles de déposer des cookies tiers lors de leur activation.
        </p>
        <div className="bg-[#080C1E] rounded-lg border border-white/[0.05] p-4 mt-3">
          <p className="text-white/40 text-xs">
            Ces cookies ne sont déposés que si vous interagissez activement avec les boutons de partage. Ils sont soumis aux politiques de confidentialité des réseaux sociaux concernés. AD.NOVA.PRO ne contrôle pas les cookies déposés par ces tiers.
          </p>
        </div>

        <p className="font-semibold text-white/80 mt-6 mb-3">2.4 — Cookies publicitaires</p>
        <div className="bg-[#080C1E] rounded-lg border border-[#C9A227]/20 p-4">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-checkbox-circle-fill text-[#C9A227] text-sm"></i>
            <span className="text-white/60 text-xs font-semibold">Aucun cookie publicitaire</span>
          </div>
          <p className="text-white/40 text-xs">
            À ce jour, le site lereseausportif.com n&apos;utilise aucun cookie de ciblage publicitaire ou de reciblage (retargeting).
          </p>
        </div>
      </Article>

      <Article num="3" title="Durée de conservation des cookies">
        <p>
          Conformément au référentiel CNIL, la durée de conservation des cookies est limitée :
        </p>
        <ul className="list-disc list-inside space-y-1 text-white/60 pl-2">
          <li>Les cookies de session sont supprimés automatiquement à la fermeture du navigateur</li>
          <li>Les cookies persistants sont conservés pour une durée maximale de <strong className="text-white/70">13 mois</strong> à compter de leur dépôt</li>
          <li>Le consentement aux cookies est valable pour une durée maximale de <strong className="text-white/70">6 mois</strong>, après quoi il est demandé à nouveau</li>
        </ul>
        <p>
          Les durées spécifiques de chaque cookie sont précisées dans le tableau de l&apos;article 2.
        </p>
      </Article>

      <Article num="4" title="Base légale par catégorie">
        <div className="space-y-3">
          <div className="bg-[#080C1E] rounded-lg border border-white/[0.05] p-4">
            <p className="font-semibold text-white/80 text-xs mb-1">Cookies essentiels</p>
            <p className="text-white/50 text-xs">Base légale : <strong>Intérêt légitime</strong> et <strong>exécution du contrat</strong> (article 6.1.b et 6.1.f du RGPD). Exemptés de consentement conformément à la délibération CNIL n° 2020-092 du 17 septembre 2020.</p>
          </div>
          <div className="bg-[#080C1E] rounded-lg border border-white/[0.05] p-4">
            <p className="font-semibold text-white/80 text-xs mb-1">Cookies analytics</p>
            <p className="text-white/50 text-xs">Base légale : <strong>Consentement</strong> de l&apos;utilisateur (article 6.1.a du RGPD). Soumis à accord préalable via le bandeau de consentement.</p>
          </div>
          <div className="bg-[#080C1E] rounded-lg border border-white/[0.05] p-4">
            <p className="font-semibold text-white/80 text-xs mb-1">Cookies réseaux sociaux</p>
            <p className="text-white/50 text-xs">Base légale : <strong>Consentement</strong> de l&apos;utilisateur (article 6.1.a du RGPD). Les cookies tiers sont soumis aux conditions des plateformes concernées.</p>
          </div>
        </div>
      </Article>

      <Article num="5" title="Comment accepter, refuser ou paramétrer les cookies ?">
        <p className="font-semibold text-white/80">5.1 — Via le bandeau de consentement</p>
        <p>
          Lors de votre première visite sur lereseausportif.com, un bandeau de consentement vous permet d&apos;accepter ou refuser les cookies non essentiels. Vous pouvez modifier vos préférences à tout moment.
        </p>

        <p className="font-semibold text-white/80 mt-4">5.2 — Via les paramètres de votre navigateur</p>
        <p>
          Vous pouvez configurer votre navigateur pour accepter ou refuser tous les cookies, ou pour être averti avant chaque dépôt. Voici les liens vers les paramètres des principaux navigateurs :
        </p>
        <ul className="space-y-1 text-white/60 pl-2 mt-2">
          <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="nofollow noopener noreferrer" className="text-[#C9A227] hover:underline">Google Chrome</a></li>
          <li><a href="https://support.mozilla.org/fr/kb/protection-renforcee-contre-le-pistage-firefox" target="_blank" rel="nofollow noopener noreferrer" className="text-[#C9A227] hover:underline">Mozilla Firefox</a></li>
          <li><a href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" target="_blank" rel="nofollow noopener noreferrer" className="text-[#C9A227] hover:underline">Safari</a></li>
          <li><a href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="nofollow noopener noreferrer" className="text-[#C9A227] hover:underline">Microsoft Edge</a></li>
        </ul>
        <p className="text-white/40 text-xs mt-2">
          Attention : la désactivation de certains cookies essentiels peut altérer le bon fonctionnement de la plateforme, notamment la connexion à votre espace membre.
        </p>

        <p className="font-semibold text-white/80 mt-4">5.3 — Via la plateforme de l&apos;outil analytics</p>
        <p>
          Si un outil analytics est configuré, vous pouvez vous y opposer directement via le module de désactivation proposé par le prestataire concerné. Ces informations seront précisées lors de l&apos;activation de l&apos;outil.
        </p>
      </Article>

      <Article num="6" title="Contact et réclamations">
        <p>
          Pour toute question relative à l&apos;utilisation des cookies sur lereseausportif.com, vous pouvez contacter l&apos;éditeur :
        </p>
        <div className="bg-[#080C1E] rounded-lg border border-white/[0.05] p-4 mt-2">
          <p className="text-xs text-white/60"><strong className="text-white/80">MADAME AURELIE PINTO — AD.NOVA.PRO</strong></p>
          <p className="text-xs text-white/60">2 allée des jardins de Courrejean, 33140 Villenave-d&apos;Ornon</p>
          <p className="text-xs text-white/60">Téléphone : 06 14 12 68 69</p>
          <p className="text-xs">Email : <a href="mailto:ad.nova.pro33@gmail.com" className="text-[#C9A227] hover:underline">ad.nova.pro33@gmail.com</a></p>
        </div>
        <p>
          En cas de réclamation relative au traitement de vos données, vous pouvez saisir la CNIL à l&apos;adresse{' '}
          <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="nofollow noopener noreferrer" className="text-[#C9A227] hover:underline">www.cnil.fr</a> ou par courrier : CNIL, 3 place de Fontenoy – TSA 80715 – 75334 Paris Cedex 07.
        </p>
      </Article>

      <Article num="7" title="Lien vers la politique de confidentialité">
        <p>
          La gestion des cookies s&apos;inscrit dans le cadre plus général du traitement de vos données personnelles par AD.NOVA.PRO. Pour en savoir plus sur la manière dont vos données sont collectées, utilisées et protégées, consultez notre <a href="/legal/mentions-legales" className="text-[#C9A227] hover:underline">page Mentions légales</a> (section Protection des données personnelles).
        </p>
      </Article>

      <Article num="8" title="Mise à jour de la politique cookies">
        <p>
          La présente politique de gestion des cookies peut être mise à jour à tout moment, notamment en cas d&apos;évolution de la réglementation ou de modification des outils utilisés sur le site. La date de dernière mise à jour est indiquée en bas de page.
        </p>
        <p>
          Nous vous invitons à consulter régulièrement cette page pour rester informé(e) des pratiques en vigueur.
        </p>
      </Article>

      <div className="mt-12 pt-8 border-t border-white/[0.07] text-center">
        <p className="text-xs text-white/25">Politique cookies mise à jour en mars 2026 — Référentiel CNIL 2020 — lereseausportif.com</p>
      </div>
    </LegalLayout>
  );
}
